import React, { useState, useEffect } from "react"
import { usePublicViewContext } from "../../context/usePublicViewContext"
import { useSaveCustomer } from "../../hooks/Costumer/useSaveCustomer"
import { useTranslation } from "react-i18next"
import { useCreatePerson } from "../../hooks/People/useCreatePerson"
import { handleChangeClearInvalid, handleInvalid } from "../Helpers/Validators"
import { EGenderType } from "../../Enum/EGenderType"
import { PersonEntity } from "../../Objects/Person/PersonEntity"
import { CustomerEntity } from "../../Objects/Customer/CustomerEntity"
import { useError } from "../../context/useErrorContext"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { useParams } from "react-router-dom"
import { useCustomerAddresses } from "../../hooks/Costumer/useCustomerAddresses"
import { AddressManager } from "../Components/AddressManager"
import { AwardProgressCard } from "../Components/AwardProgressCard"
import { LoginRedirectView } from "../Components/LoginRedirectView"
import { EEnableStatusType } from "../../Enum/EEnableStatusType"

export const ProfileView: React.FC = () => {
  const { storeSlug } = useParams<{ storeSlug: string }>()

  const { t } = useTranslation("person")

  const { auth, logout, updateCustomer, setCurrentView } = usePublicViewContext()

  const [isEditing, setIsEditing] = useState(false)

  const { handleSavePublicCostumer } = useSaveCustomer()

  const { setError } = useError()

  const { addresses } = useCustomerAddresses(storeSlug)

  const {
    name,
    cpf,
    phone,
    email,
    dateBorn,
    gender,
    handleNameChange,
    handleCpfChange,
    setGender,
    handlePhoneChange,
    handleEmailChange,
    handleDateBornChange,
    handleSubmitForm,
    handleInitialValue,
  } = useCreatePerson()

  const customer = auth.customer

  const person = customer?.personEntity
  const activeAwardManagers = (customer?.awardManagers ?? []).filter(
    (awardManager) =>
      awardManager.status === EEnableStatusType.Active && awardManager.awardEntity?.status === EEnableStatusType.Active,
  )

  useEffect(() => {
    if (!person) {
      return
    }

    handleInitialValue(person)
  }, [person, handleInitialValue])

  const handleSave = (rote: string = API_ROUTES.CUSTOMER.PUT_CUSTOMER_PUBLIC(storeSlug)) => {
    const person: PersonEntity = handleSubmitForm()

    if (!auth.customer) {
      logout()

      return
    }

    const customer: CustomerEntity = new CustomerEntity(auth.customer)

    person.addresses = addresses

    if (!person || !customer) {
      setError(t("general:label_title_error"), t("general:label_description_error"))

      return
    }

    customer.personEntity = person

    handleSavePublicCostumer(rote, customer, (updatedCustomer: CustomerEntity) => {
      setIsEditing(false)

      updateCustomer(updatedCustomer)
    })
  }

  if (!customer || !person) {
    return <LoginRedirectView setCurrentView={setCurrentView} />
  }

  return (
    <div className="max-w-4xl mx-auto p-3 pb-32 space-y-12 animate-in fade-in duration-700">
      <div className="bg-[#0A0A0A] rounded-2xl p-5 md:p-12 text-white shadow-2xl relative overflow-hidden ring-1 ring-white/5">
        <div className="absolute top-0 right-0 w-96 h-96 bg-orange-600/10 rounded-full -mr-5 -mt-48 blur-[100px] pointer-events-none" />

        <div className="relative z-10 flex flex-col items-center md:flex-row gap-10">
          <div className="shrink-0 relative">
            <div className="w-32 h-32 md:w-40 md:h-40 bg-white rounded-[3rem] flex items-center justify-center text-[#121212] text-6xl font-black shadow-2xl">
              {person.name.charAt(0)}
            </div>

            <div className="absolute -bottom-2 -right-2 bg-orange-600 p-3 rounded-2xl border-[6px] border-[#0A0A0A] shadow-lg">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            </div>
          </div>

          <div className="flex-1 w-full text-center md:text-left">
            {isEditing ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  handleSave()
                }}
                className="space-y-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-widest text-orange-500 px-2">
                      {t("publicMenu:profile.full_name")}
                    </label>

                    <input
                      type="text"
                      required
                      value={name}
                      onChange={handleChangeClearInvalid(handleNameChange)}
                      placeholder={t("label_person_name_place_holder")}
                      onInvalid={handleInvalid(t("label_person_name_onInvalidMessage"))}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-6 py-4 focus:ring-2 focus:ring-orange-600 outline-none transition-all font-bold"
                      data-interaction-description-key="interactionDescriptions:publicMenu.pages.profileView.input.personNamePlaceHolder"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-widest text-orange-500 px-2">
                      {t("publicMenu:profile.email")}
                    </label>

                    <input
                      type="email"
                      value={email}
                      onChange={handleChangeClearInvalid(handleEmailChange)}
                      placeholder={t("label_person_email_place_holder")}
                      onInvalid={handleInvalid(t("label_person_email_onInvalidMessage"))}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-6 py-4 focus:ring-2 focus:ring-orange-600 outline-none transition-all font-bold"
                      data-interaction-description-key="interactionDescriptions:publicMenu.pages.profileView.input.personEmailPlaceHolder"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-widest text-orange-500 px-2">
                      {t("publicMenu:profile.cpf")}
                    </label>

                    <input
                      type="number"
                      value={cpf}
                      required
                      onChange={handleChangeClearInvalid(handleCpfChange)}
                      placeholder={t("label_person_cpf_place_holder")}
                      onInvalid={handleInvalid(t("label_person_cpf_onInvalidMessage"))}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-6 py-4 focus:ring-2 focus:ring-orange-600 outline-none transition-all font-bold"
                      data-interaction-description-key="interactionDescriptions:publicMenu.pages.profileView.input.personCpfPlaceHolder"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-widest text-orange-500 px-2">
                      {t("publicMenu:profile.phone")}
                    </label>

                    <input
                      type="number"
                      value={phone}
                      required
                      onChange={handleChangeClearInvalid(handlePhoneChange)}
                      placeholder={t("label_person_phone_place_holder")}
                      onInvalid={handleInvalid(t("label_person_phone_onInvalidMessage"))}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-6 py-4 focus:ring-2 focus:ring-orange-600 outline-none transition-all font-bold"
                      data-interaction-description-key="interactionDescriptions:publicMenu.pages.profileView.input.personPhonePlaceHolder"
                    />
                  </div>

                  <div className="space-y-1.5 min-w-0 w-full">
                    <label className="text-[10px] font-black uppercase tracking-widest text-orange-500 px-2">
                      {t("publicMenu:profile.birth_date")}
                    </label>

                    <input
                      type="date"
                      value={dateBorn}
                      onChange={handleChangeClearInvalid(handleDateBornChange)}
                      placeholder={t("label_person_date_born_place_holder")}
                      onInvalid={handleInvalid(t("label_person_date_born_onInvalidMessage"))}
                      className="block w-full min-w-0 max-w-full box-border bg-white/5 border border-white/10 rounded-lg pl-4 pr-3 py-3.5 focus:ring-2 focus:ring-orange-600 outline-none transition-all font-bold text-white [color-scheme:dark]"
                      data-interaction-description-key="interactionDescriptions:publicMenu.pages.profileView.input.personDateBornPlaceHolder"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-widest text-orange-500 px-2">
                      {t("publicMenu:profile.gender")}
                    </label>

                    <div className="flex flex-wrap gap-2">
                      {[EGenderType.Woman, EGenderType.Man].map((e: EGenderType) => (
                        <button
                          key={e}
                          type="button"
                          onClick={() => setGender(e)}
                          className={`px-4 py-2 rounded-lg text-[12px] font-black uppercase transition-all ${gender === e ? "bg-orange-600 text-white" : "bg-white/5 text-white/40 hover:bg-white/10"}`}
                          data-interaction-description-key="interactionDescriptions:publicMenu.pages.profileView.button.setGenderE"
                        >
                          {e.toGenderTypeString()}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 pt-2">
                  <button
                    type="submit"
                    className="flex-1 bg-white text-black py-4 rounded-lg text-[11px] font-black uppercase tracking-widest hover:bg-orange-600 hover:text-white transition-all"
                    data-interaction-description-key="interactionDescriptions:publicMenu.pages.profileView.button.saveProfile"
                  >
                    {t("publicMenu:profile.save_profile")}
                  </button>

                  <button
                    type="button"
                    onClick={() => setIsEditing(false)}
                    className="px-10 py-4 bg-white/10 rounded-lg ring-2 ring-red-500 text-[11px] hover:bg-black font-black uppercase tracking-widest"
                    data-interaction-description-key="interactionDescriptions:publicMenu.pages.profileView.button.setIsEditingFalse"
                  >
                    {t("publicMenu:profile.cancel")}
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-8">
                <div>
                  <div className="flex items-center justify-center md:justify-start gap-3 mb-2">
                    <span className="text-[10px] font-black uppercase tracking-[0.5em] text-orange-600">
                      {t("publicMenu:profile.membership_tier")}
                    </span>

                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 shadow-[0_0_10px_rgba(34,197,94,0.5)]"></span>
                  </div>

                  <h2 className="text-2xl font-black tracking-tighter uppercase leading-none">{person.name}</h2>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-left border-y border-white/5 py-8">
                  <div className="space-y-1">
                    <span className="text-[8px] block uppercase opacity-30 tracking-[0.2em]">
                      {t("publicMenu:profile.email")}
                    </span>

                    <span className="text-[11px] font-bold">{person.email.truncate(25)}</span>
                  </div>

                  <div className="space-y-1">
                    <span className="text-[8px] block uppercase opacity-30 tracking-[0.2em]">
                      {t("publicMenu:profile.phone")}
                    </span>

                    <span className="text-[13px] font-bold">{person.phone}</span>
                  </div>

                  <div className="space-y-1">
                    <span className="text-[8px] block uppercase opacity-30 tracking-[0.2em]">
                      {t("publicMenu:profile.cpf")}
                    </span>

                    <span className="text-[13px] font-bold">{person.cpf || "—"}</span>
                  </div>

                  <div className="space-y-1">
                    <span className="text-[8px] block uppercase opacity-30 tracking-[0.2em]">
                      {t("publicMenu:profile.gender")}
                    </span>

                    <span className="text-[13px] font-bold">{person.genderType.toGenderTypeString()}</span>
                  </div>
                </div>

                <div className="flex justify-center md:justify-start gap-4">
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-8 py-3 bg-white/5 border border-white/10 rounded-lg ring-1 ring-white text-[10px] font-black uppercase tracking-[0.2em] hover:bg-white/10 transition-all"
                    data-interaction-description-key="interactionDescriptions:publicMenu.pages.profileView.button.setIsEditingTrue"
                  >
                    {t("publicMenu:profile.edit_account")}
                  </button>
                  <button
                    onClick={logout}
                    className="px-8 py-3 bg-red-600/10 text-red-500 rounded-lg ring-1 ring-white text-[10px] font-black uppercase tracking-[0.2em] hover:bg-red-600 hover:text-white transition-all"
                    data-interaction-description-key="interactionDescriptions:publicMenu.pages.profileView.button.logout"
                  >
                    {t("publicMenu:profile.logout")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-5">
        {activeAwardManagers.length > 0 && (
          <div className="flex items-center justify-between px-2">
            <div>
              <h3 className="text-[11px] font-black text-orange-400 uppercase tracking-[0.2em] mb-1">
                {t("publicMenu:awards.loyalty_program")}
              </h3>

              <p className="text-[9px] text-gray-600 font-bold uppercase tracking-[0.2em]">
                {t("publicMenu:profile.awards_count", { count: activeAwardManagers.length })}
              </p>
            </div>
          </div>
        )}

        {activeAwardManagers.length > 0 &&
          activeAwardManagers.map((e) => <AwardProgressCard awardManager={e} key={e.id} />)}
      </div>

      <AddressManager
        title={t("publicMenu:address_list.my_destinations")}
        emptyLabel={t("publicMenu:address_list.empty_destinations")}
      />
    </div>
  )
}
